import React from 'react';
import styled from 'styled-components';

import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { TOrbit } from '../../../i18n/TModules';
import PrizeFields from '../kit/PrizeFields';
import { AddButton, RemoveButton } from '../kit/controls';
import { Card, Eyebrow } from '../kit/layout';
import { WizardPrize } from '../wizardModel';

const TP = `${TOrbit}.wizard.prize`;

const CardHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const AddRow = styled.div`
  margin-top: 14px;
`;

interface IPrizeListProps<T extends WizardPrize> {
  name: string;
  prizes: T[];
  onChange: (prizes: T[]) => void;
  eyebrow: (n: number) => string;
  addLabel: string;
  /** A new card's own fields beyond the prize (a Stamp slot, a Game probability). */
  blank: () => T;
  rewardQuery?: Record<string, unknown>;
}

/** 6778:22620 / :24207: a card per prize, all issued together, and a dashed "Add prize". */
const PrizeList = <T extends WizardPrize>({
  name,
  prizes,
  onChange,
  eyebrow,
  addLabel,
  blank,
  rewardQuery
}: IPrizeListProps<T>) => {
  const { t } = useMultiLanguageTranslation();
  return (
    <>
      {prizes.map((prize, index) => (
        // Cards have no identity of their own; removing one never reorders the rest.
        <Card key={index} data-testid="orbit-wizard-prize-card">
          <CardHead>
            <Eyebrow>{eyebrow(index + 1)}</Eyebrow>
            {prizes.length > 1 ? (
              <RemoveButton
                label={t(`${TP}.remove`, { n: index + 1 })}
                onClick={() => onChange(prizes.filter((_, at) => at !== index))}
              />
            ) : null}
          </CardHead>
          <PrizeFields<T>
            name={`${name}-${index}`}
            prize={prize}
            rewardQuery={rewardQuery}
            onChange={value => onChange(prizes.map((item, at) => (at === index ? value : item)))}
          />
        </Card>
      ))}
      <AddRow>
        <AddButton label={addLabel} onClick={() => onChange([...prizes, blank()])} />
      </AddRow>
    </>
  );
};

export default PrizeList;
